import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, PasswordResponses } from 'react-pdf';
import Spinner from '~/components/Spinner';
import FileErrorIcon from '~/components/icons/FileErrorIcon';
import { gqlQueryClient } from '~/lib/backend';
import { getHumanReadableFormAndDocumentType } from '~/lib/files';
import meQuery from '~/queries/meQuery';
import { Link, useNavigate, useParams } from '~/router';

export default function GloveCompartmentDocumentPage() {
  const { t } = useTranslation();
  // We need to use refs to determine the width for the PDFs as the classNames are not respected for the width
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { transactionId, documentId, dealershipSlug } = useParams(
    '/dashboard/:dealershipSlug/:transactionId/glove-compartment/document/:documentId'
  );
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(0);
  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };
  const { data: userData, isLoading: loadingUserData } = useQuery(meQuery());
  const [giveUpOnPw, setGiveUpOnPw] = useState(false);

  const { data: formSubmissionsData, isLoading: loadingFormSubmissions } =
    useQuery({
      refetchOnReconnect: false,
      refetchInterval: false,
      enabled: Boolean(userData?.me?.user?.id),
      queryKey: [
        'transaction',
        transactionId,
        'customer',
        'documents',
        documentId,
        dealershipSlug
      ],
      queryFn: async () =>
        gqlQueryClient({ dealershipSlug })({
          customer: [
            {
              userId: userData?.me?.user?.id ?? '', // For typing, the query is only enabled when this is set
              transactionId
            },
            {
              documents: [
                {
                  filter: {
                    id: {
                      equals: documentId
                    }
                  }
                },
                {
                  edges: {
                    node: {
                      id: true,
                      category: true,
                      file: {
                        url: true
                      }
                    }
                  }
                }
              ]
            }
          ]
        })
    });
  const formSubmission = formSubmissionsData?.customer?.documents?.edges?.at(0);

  /**
   *
   * @param callback
   * @param reason There is an enum for this
   */
  const onPassword = (callback: (pw: string) => void, reason: number) => {
    switch (reason) {
      case PasswordResponses.NEED_PASSWORD: {
        const password = prompt('Enter the password to open this PDF file.');
        if (!password) {
          setGiveUpOnPw(true);
          return;
        }
        callback(password);
        break;
      }
      case PasswordResponses.INCORRECT_PASSWORD: {
        const password = prompt('Invalid password. Please try again.');
        if (!password) {
          setGiveUpOnPw(true);
          return;
        }
        callback(password);
        break;
      }
      default:
    }
  };

  return (
    <div className="flex flex-col h-dvh">
      {/* We are loading the page */}
      {!giveUpOnPw && (loadingUserData || loadingFormSubmissions) && (
        <div className="flex w-1/3 aspect-square items-center justify-center">
          <Spinner />
        </div>
      )}

      {/* User gave up on password */}
      {giveUpOnPw && (
        <div className="flex items-center w-full justify-center p-spacing-09">
          <div
            className={
              'py-spacing-02 px-spacing-04 flex gap-spacing-04 items-center justify-center rounded-radius-02 border bg-warning-light border-warning-dark'
            }
          >
            <FileErrorIcon className="text-warning-dark w-6 h-6 flex-shrink-0" />
            <p>
              {t('Password required in order to view password protected files')}
            </p>
          </div>
        </div>
      )}

      {!giveUpOnPw && !loadingUserData && !loadingFormSubmissions && (
        <div className="flex flex-col p-8 items-center">
          <div
            ref={containerRef}
            className="flex flex-col w-full max-w-full md:max-w-screen-md"
          >
            <div className="flex flex-row items-center py-spacing-04 space-x-spacing-02">
              <Link
                to="/dashboard/:dealershipSlug/:transactionId/glove-compartment"
                params={{ dealershipSlug, transactionId }}
                className="link-large"
              >
                {t('All Documents')}
              </Link>

              <div className="h-2/3 border border-primary" />

              <h3 className="heading-03">
                {getHumanReadableFormAndDocumentType(
                  formSubmission?.node?.category ?? t('Document'),
                  t
                )}
              </h3>
            </div>

            <Document
              onPassword={onPassword}
              file={formSubmission?.node?.file?.url}
              onLoadSuccess={handleDocumentLoadSuccess}
              className="flex flex-col space-y-1"
              loading={
                <div className="flex w-full min-h-[80vh] justify-center items-center">
                  <Spinner />
                </div>
              }
              error={
                <div className="flex flex-col w-full min-h-[80vh] justify-center items-center">
                  <div className="flex flex-col space-y-4 items-center">
                    <div className="relative">
                      <FileErrorIcon className="w-20 icon-negative" />
                    </div>

                    <p className="text-negative">
                      {t('Failed to load document')}
                    </p>
                  </div>
                </div>
              }
            >
              {[...new Array(numPages)].map((_, index) => (
                <Page
                  key={index}
                  pageNumber={index + 1}
                  className="border"
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  width={containerRef.current?.clientWidth}
                />
              ))}
            </Document>
          </div>
        </div>
      )}
    </div>
  );
}
